"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useLocale } from "next-intl";
import {
  generateLinkFromGeneralLink,
  generateTargetFromLink,
  LinkConfig,
} from "@/lib/utils";
import {
  ComponentMoleculesLabelLink,
  ContentJapanHotelTab,
  Enum_Componentmoleculeshoteltabitem_Use_Existing_Tab,
  NavigationDatasourcesQuery,
} from "@/types/generated";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/atoms/accordian";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTrigger,
} from "@/components/atoms/drawer";
import { Button } from "@/components/atoms/button";
import { Icon } from "@/components/atoms/icon";
import { cn } from "@/lib/utils";
import { EXISTING_TAB_URL_MAP, ResolvedTab } from "../../hotel-details/layout/CustomTabsNav";
import { Check, Globe, Minus, Plus } from "lucide-react";
import { useAuth } from "@/hooks/useAuth/useAuth";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import { MOBILE_BREAKPOINT } from "@/constants";
import {
  GO_TO_PASS_ITEM_NAME,
} from "@/utils";
import Image from "next/image";
import {
  getPathname,
  Locale,
  LOCALE_NAMES,
  LOCALE_SELECT_PLACEHOLDER,
  routing,
} from "@/i18n/routing";

interface NavMenu {
  itemName?: string;
  title?: string | null;
  url?: string | null;
  icon?: {
    cloudflare_url?: string | null;
    url?: string | null;
    alt_text?: string | null;
    caption?: string | null;
  };
}

interface TemplateHotelSpecificMenuProps {
  additionalMenuLinks?: Partial<ComponentMoleculesLabelLink>[] | null;
  customTabsData?: ContentJapanHotelTab[] | null;
  hotelSlug: string;
  isAttractionTemplate?: boolean;
  navigationDatasources?: Omit<NavigationDatasourcesQuery, "__typename">;
  isShowGotoPass?: boolean | null;
}

const TemplateHotelSpecificMenu = ({
  additionalMenuLinks,
  customTabsData,
  hotelSlug,
  isAttractionTemplate = false,
  navigationDatasources,
  isShowGotoPass,
}: TemplateHotelSpecificMenuProps) => {
  const locale = useLocale();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [currentHash, setCurrentHash] = useState("");
  const { userStore: user } = useAuth();
  const isMobile = useIsMobile(MOBILE_BREAKPOINT);


  const onChangeLanguage = useCallback(
      (nextLocale: Locale) => {
        const url = getPathname({
          href: { pathname, query: {} },
          locale: nextLocale,
        });
        globalThis.location.assign(url);
      },
      [pathname],
    );

  useEffect(() => {
    setCurrentHash(window.location.hash);
    const onHashChange = () => setCurrentHash(window.location.hash);
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const filteredParams = new URLSearchParams(searchParams?.toString() || "");
  filteredParams.delete("attractionId");
  const queryString = filteredParams.toString()
    ? `?${filteredParams.toString()}`
    : "";

  const documentId = isMobile
    ? "65804619fec40b3fa0541ee4"
    : "72ec2890fa3a6dade80b3fcf";

  const { excludedMenuItems, topItems } = useMemo(() => {
    const navSources =
      navigationDatasources?.mystay?.shared_datasource
        ?.navigation_datasources ?? [];

    const sideNavigation = navSources.find((n) => n?.documentId === documentId);

    const selectedMenu = sideNavigation?.display_menu_items ?? [];
    const menuItems = sideNavigation?.menu_items ?? [];

    const mapMenu = (menuItem: any): NavMenu | null => {
      if (!menuItem) return null;
      const link = user
        ? menuItem.button_link_after_loggin || menuItem.menu_link
        : menuItem.menu_link;

      return {
        itemName: menuItem.item_name,
        title: user
          ? menuItem.title_after_loggin || menuItem.menu_title || null
          : menuItem.menu_title || null,
        url: generateLinkFromGeneralLink(link, locale) || "#",
        icon: menuItem.icon ?? {},
      };
    };

    const sortedMenus: NavMenu[] = [
      ...selectedMenu
        .map((s: any) =>
          mapMenu(menuItems.find((m: any) => m?.documentId === s.documentId)),
        )
        .filter(Boolean),
      {
        itemName: "customerTestimonial",
        title: "お客様の声",
        url: `/${locale}/hotelreviewsection`,
        icon: {},
      },
    ];

    const excluded = ["01_Home", "02_Hotel List"];

    const top = sortedMenus.filter(
      (i) => i?.itemName && !excluded.includes(i.itemName),
    );

    const excludedList = sortedMenus.filter((i) =>
      excluded.includes(i?.itemName as string),
    );

    return {
      topItems: top,
      excludedMenuItems: excludedList,
    };
  }, [navigationDatasources, documentId, user, locale]);

  const resolveTabHref = useCallback(
    (tabItem: {
      use_existing_tab?: Enum_Componentmoleculeshoteltabitem_Use_Existing_Tab | null;
      use_custom_link?: boolean | null;
      custom_link?: unknown;
    }): { href: string; target: "_blank" | "_self" | "_parent" | "_top" } => {
      if (tabItem.use_custom_link && tabItem.custom_link) {
        return {
          href: generateLinkFromGeneralLink(tabItem.custom_link as LinkConfig, locale),
          target: generateTargetFromLink(tabItem.custom_link as LinkConfig),
        };
      }
      if (tabItem.use_existing_tab) {
        if (tabItem.use_existing_tab === "attractions") {
          return {
            href: isAttractionTemplate
              ? `/${locale}/attraction-${hotelSlug}${queryString}`
              : `/${locale}/location-${hotelSlug}${queryString}#attractions`,
            target: "_self",
          };
        }
        const prefix = EXISTING_TAB_URL_MAP[tabItem.use_existing_tab] ?? "";
        return {
          href: `/${locale}/${prefix}${hotelSlug}${queryString}`,
          target: "_self",
        };
      }
      return { href: "#", target: "_self" };
    },
    [locale, hotelSlug, isAttractionTemplate, queryString],
  );

  const resolvedTabs: ResolvedTab[] = useMemo(() => {
    return (customTabsData || [])
    .map((tab) => {
      const { href, target } = resolveTabHref(tab.tab_data);
      const title =
        tab.tab_data?.label || tab.item_name || tab.display_name || "";
      const subtabs = (tab.subtabs || [])
        .filter(Boolean)
        .map((subtab) => {
          const { href: subHref, target: subTarget } = resolveTabHref(subtab!);
          return {
            title: subtab?.label || "",
            href: subHref,
            isActive: false,
            target: subTarget,
          };
        });
      return { title, href, isActive: false, target, subtabs };
    });
  }, [customTabsData, resolveTabHref]);

  const resolvedAdditionalLinks = useMemo(() => {
    return (additionalMenuLinks || [])
      .filter((item) => item.label && item.link)
      .map((item) => ({
        label: item.label!,
        href: generateLinkFromGeneralLink(item.link as LinkConfig, locale) || "#",
        target: generateTargetFromLink(item.link as LinkConfig),
      }));
  }, [additionalMenuLinks, locale]);

  const hasCustomTabs = resolvedTabs.length > 0;
  const hasAdditionalLinks = resolvedAdditionalLinks.length > 0;
  const hasExcludedItems = excludedMenuItems.length > 0;
  const hasTopItems = !!isShowGotoPass && topItems.some((i) => i.itemName === GO_TO_PASS_ITEM_NAME);

  if (!hasCustomTabs && !hasAdditionalLinks && !hasExcludedItems && !hasTopItems) return null;

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) document.documentElement.style.scrollBehavior = "auto";
    setTimeout(() => {
      document.documentElement.style.scrollBehavior = "smooth";
    }, 1000);
  };

  const handleNavigate = (href: string, target: string) => {
    if (target === "_blank") {
      window.open(href, "_blank");
    } else {
      globalThis.location.href = href;
    }
  };

  const hasItemsAboveDivider = hasCustomTabs || hasAdditionalLinks;
  const hasItemsBelowDivider = hasExcludedItems || hasTopItems;

  return (
    <Drawer onOpenChange={handleDrawerOpenChange}>
      {/* Hamburger trigger — identical to MobileMenu */}
      <div className="flex flex-col items-center justify-center">
        <DrawerTrigger asChild>
          <Button
            className="h-6 p-0 hover:bg-white active:bg-white"
            hierarchy="ghost"
            aria-label="Open Menu"
          >
            <Icon icon="menu" />
          </Button>
        </DrawerTrigger>
        <div className="text-2xs leading-3 text-[var(--fill-base-main)] uppercase">
          Menu
        </div>
      </div>

      <DrawerContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="fixed top-0 right-0 h-full w-80 bg-[var(--fill-base-main)]"
      >
        <div className="flex flex-col overflow-y-auto px-6 py-4">

          {/* Close button — identical to MobileMenu */}
          <div className="flex items-center justify-end py-2 pl-4 mb-4">
            <DrawerClose asChild>
              <Button
                hierarchy="outline"
                className="h-9 w-9 rounded-full border-none p-0"
              >
                <Icon icon="close" />
              </Button>
            </DrawerClose>
          </div>

          <nav className="flex flex-col items-start">
            {/* Custom Tabs */}
            {hasCustomTabs && resolvedTabs.map((tab, index) => {
              const hasSubtabs = tab.subtabs.length > 0;

              if (!hasSubtabs) {
                return (
                  <DrawerClose asChild key={`tab-${index}`}>
                    <Button
                      hierarchy="ghost"
                      className={cn(
                        "w-full text-base justify-start py-4 text-[var(--text-gray-base)]",
                        "hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]",
                        "active:bg-[var(--fill-base-white)] active:text-[var(--text-base-primary)]",
                      )}
                      onClick={() => handleNavigate(tab.href, tab.target)}
                    >
                      <span>{tab.title}</span>
                    </Button>
                  </DrawerClose>
                );
              }

              return (
                <Accordion
                  key={`tab-accordion-${index}`}
                  type="single"
                  collapsible
                  className="w-full"
                >
                  <AccordionItem value={`tab-${index}`} className="border-none">
                    <AccordionTrigger
                      className={cn(
                        "px-4 py-1.5 hover:cursor-pointer rounded text-base hover:bg-[var(--fill-base-white)] hover:no-underline",
                        "hover:text-[var(--text-base-primary)]",
                        "text-[var(--text-gray-base)]",
                      )}
                      openIcon={<Minus className="size-5" />}
                      closeIcon={<Plus className="size-5" />}
                    >
                      {tab.title}
                    </AccordionTrigger>
                    <AccordionContent className="pb-0">
                      <div className="flex flex-col items-start">
                        {tab.subtabs.map((subtab, subIdx) => (
                          <DrawerClose asChild key={`subtab-${index}-${subIdx}`}>
                            <Button
                              hierarchy="ghost"
                              className={cn(
                                "w-full justify-start py-3 pl-8 pr-4",
                                "text-[var(--text-gray-base)]",
                                "hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]",
                                "active:bg-[var(--fill-base-white)] active:text-[var(--text-base-primary)]",
                              )}
                              onClick={() => handleNavigate(subtab.href, subtab.target)}
                            >
                              <span>{subtab.title}</span>
                            </Button>
                          </DrawerClose>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              );
            })}

            {/* Additional Menu Links */}
            {hasAdditionalLinks && resolvedAdditionalLinks.map((item, index) => (
              <DrawerClose asChild key={`additional-${index}`}>
                <Button
                  hierarchy="ghost"
                  className={cn(
                    "w-full text-base justify-start py-4 text-[var(--text-gray-base)]",
                    "hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]",
                    "active:bg-[var(--fill-base-white)] active:text-[var(--text-base-primary)]",
                  )}
                  onClick={() => handleNavigate(item.href, item.target)}
                >
                  <span>{item.label}</span>
                </Button>
              </DrawerClose>
            ))}

            {/* Divider — shown when there are items above AND below */}
            {hasItemsAboveDivider && hasItemsBelowDivider && (
              <div className="my-6 h-px w-full bg-[var(--fill-base-white)] px-4 opacity-60" />
            )}

            {/* Excluded Menu Items (01_Home, 02_Hotel List) */}
            {excludedMenuItems.map((menu, i) => {
              const customTitle =
                menu.itemName === "01_Home"
                  ? "アイコニア・ホスピタリティ トップ"
                  : menu.itemName === "02_Hotel List"
                    ? "ホテル一覧"
                    : menu.title;

              return (
                <DrawerClose asChild key={`excluded-${i}`}>
                  <Button
                    hierarchy="ghost"
                    className="w-full justify-start py-4 text-[var(--text-gray-base)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)] active:bg-[var(--fill-base-white)] active:text-[var(--text-base-primary)]"
                    onClick={() => handleNavigate(menu.url ?? "#", "_self")}
                  >
                    {customTitle}
                  </Button>
                </DrawerClose>
              );
            })}

            {/* GoToPass Button */}
            {!!isShowGotoPass &&
              topItems
                .filter((i) => i.itemName === GO_TO_PASS_ITEM_NAME)
                .map((menu, i) => (
                  <DrawerClose asChild key={`goto-${i}`}>
                    <button
                      onClick={() => handleNavigate(menu.url ?? "#", "_self")}
                      className="mt-4 flex h-[50px] w-full cursor-pointer items-center justify-center gap-1 bg-[var(--button-go-to-pass-bg)] px-1 py-4 text-white"
                    >
                      <p className="text-sm">{menu.title}</p>
                      {(menu.icon?.cloudflare_url || menu.icon?.url) && (
                        <div className="relative h-5 min-w-5">
                          <Image
                            src={menu.icon.cloudflare_url || menu.icon.url || ""}
                            alt={menu.icon.alt_text || ""}
                            aria-label={menu.icon.caption || ""}
                            fill
                          />
                        </div>
                      )}
                    </button>
                  </DrawerClose>
                ))}

                <div className="mt-4 flex flex-col gap-1.5 w-full">
                            <Accordion type="single" collapsible className="w-full">
                              <AccordionItem value="language" className="border-none">
                                <AccordionTrigger className="my-3 w-full min-w-[184.75px] justify-between rounded-lg px-4 py-2 font-[700] text-[var(--text-gray-base)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]">
                                  <div className="flex items-center gap-2">
                                    <Globe className="h-5 w-5" />
                                    <span data-wovn-ignore>{LOCALE_SELECT_PLACEHOLDER}</span>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent>
                                  {routing.locales.map((lang) => (
                                    <div
                                      key={lang}
                                      data-wovn-ignore
                                      aria-current={locale === lang ? "true" : undefined}
                                      onClick={() => onChangeLanguage(lang)}
                                      className={cn(
                                        "my-2 flex w-full cursor-pointer items-center justify-between rounded-lg px-2 py-1 text-[var(--text-gray-lighter)] hover:bg-[var(--fill-base-white)] hover:text-[var(--text-base-primary)]",
                                        locale === lang &&
                                          "bg-[var(--fill-base-white)] text-[var(--text-base-primary)]",
                                      )}
                                    >
                                      <span>{LOCALE_NAMES[lang]}</span>
                                      {locale === lang && <Check className="h-4 w-4 shrink-0" />}
                                    </div>
                                  ))}
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          </div>
          </nav>

        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default TemplateHotelSpecificMenu;